import { useMutation, useQuery } from "@tanstack/react-query";
import { useState, useMemo } from "react";
import { QUERY_KEYS } from "~/constants/queryKeys";
import { publicApiClient } from "~/lib/orpc-client";
import { FilterAiUsageOptionsByRoles } from "~/utils/filter-ai-usage-options";
import { COLLABORATOR_ROLES } from "~/types/collaborator-role";

type CollaboratorRole = (typeof COLLABORATOR_ROLES)[number];

export const useDeclarationInvitation = (token: string) => {
  const [showSuccess, setShowSuccess] = useState(false);
  const [declarationsChecked, setDeclarationsChecked] = useState<
    Record<string, boolean>
  >({});

  const {
    data: tokenData,
    isLoading: verifyLoading,
    error: verifyError,
  } = useQuery({
    queryKey: [...QUERY_KEYS.COLLABORATORS, "verify", token],
    queryFn: () => publicApiClient.collaborators.verifyToken({ token }),
    enabled: !!token,
  });

  const declarationItems = useMemo(() => {
    if (!tokenData?.collaborator?.roles) {
      return [];
    }

    return FilterAiUsageOptionsByRoles(
      tokenData.collaborator.roles as CollaboratorRole[],
      "PUBLICATION"
    );
  }, [tokenData?.collaborator?.roles, tokenData?.application?.category]);

  const allDeclarationsChecked = useMemo(() => {
    if (declarationItems.length === 0) return false;
    return declarationItems.every((item) => declarationsChecked[item.role]);
  }, [declarationItems, declarationsChecked]);

  const handleDeclarationChange = (role: string, checked: boolean) => {
    setDeclarationsChecked((prev) => ({ ...prev, [role]: checked }));
  };

  const completeDeclarationMutation = useMutation({
    mutationFn: () => publicApiClient.collaborators.completeDeclaration({ token }),
    onSuccess: () => {
      setShowSuccess(true);
    },
    onError: (error: Error) => {
      console.error(error.message || "Failed to submit declaration");
    },
  });

  const contentType = tokenData?.contentType ?? null;

  return {
    tokenData,
    verifyLoading,
    verifyError,
    showSuccess,
    declarationItems,
    declarationsChecked,
    allDeclarationsChecked,
    handleDeclarationChange,
    completeDeclarationMutation,
    contentType,
  };
};
